import React, { useEffect, useRef, useState } from "react";
import { fetchTenantConfigs, updateTenantConfigs } from "../../api/tenantConfigApi";

const DEFAULT_TTL_MINUTES = 2880;
export const DEFAULT_EMAIL_BODY = "Hello,\n\nA consent form has been completed and signed.\n\nPatient name: {{ patient_name }}\nForm: {{ form_title }}\nReference: {{ request_reference }}\nSigned on: {{ signed_at_local }}\n\nThe signed PDF is attached for your records.\nPlease handle this document confidentially.";
const SAMPLE_VALUES = {
  patient_name: "Alex Patient", clinic_name: "Example Clinic", form_title: "Treatment consent",
  request_reference: "123", signed_at_local: "23 Oct 2026, 06:00 PM IST",
};
const bodyError = (value) => {
  if (typeof value !== "string" || !value.trim() || value.length > 10000) return "Email body must contain 1 to 10000 characters.";
  if (Array.from(value).some((char) => char.charCodeAt(0) < 32 && !"\n\r\t".includes(char))) {
    return "Email body contains unsupported control characters.";
  }
  let unknown = "";
  const remainder = value.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (token, key) => {
    if (!Object.prototype.hasOwnProperty.call(SAMPLE_VALUES, key)) unknown = key;
    return "";
  });
  if (unknown) return `Unknown email placeholder: ${unknown}.`;
  if (/\{\{|\}\}|\{%|%\}|\{#|#\}/.test(remainder)) return "Use only the supported placeholders; expressions are not allowed.";
  return "";
};
const EMAIL_LOCAL_PART = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const EMAIL_DOMAIN = /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;

const parseRecipients = (value) => {
  if (typeof value !== "string" || /[\r\n]/.test(value)) return { recipients: [], error: "Enter email addresses separated by commas on one line." };
  if (!value.trim()) return { recipients: [], error: "" };
  const parts = value.split(",");
  if (parts.some((part) => !part.trim())) return { recipients: [], error: "Remove empty entries between commas." };
  const recipients = [];
  const seen = new Set();
  for (const part of parts) {
    const recipient = part.trim();
    const [localPart, domain, ...extra] = recipient.split("@");
    if (extra.length || !localPart || localPart.length > 64 || recipient.length > 254
      || !EMAIL_LOCAL_PART.test(localPart) || !EMAIL_DOMAIN.test(domain || "")) {
      return { recipients: [], error: `Enter a valid email address: ${recipient}` };
    }
    const key = recipient.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      recipients.push(recipient);
    }
  }
  if (recipients.length > 50) return { recipients: [], error: "Enter no more than 50 unique email recipients." };
  return { recipients, error: "" };
};

export default function ConsentFormSettings({ botId, disabled = false }) {
  const [minutes, setMinutes] = useState(String(DEFAULT_TTL_MINUTES));
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_BODY);
  const [otpTemplateName, setOtpTemplateName] = useState("");
  const [otpTemplateLanguage, setOtpTemplateLanguage] = useState("en_US");
  const bodyInput = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadFailed(false);
    setError("");
    setSaved(false);
    fetchTenantConfigs(botId).then((response) => {
      if (!active) return;
      const configs = response?.data || response;
      if (!Array.isArray(configs)) throw new Error("Invalid bot settings response.");
      const config = configs.find((item) => item.config_key === "consent_forms");
      const settings = config ? config.config_json : {};
      if (!settings || typeof settings !== "object" || Array.isArray(settings)) {
        throw new Error("Invalid consent TTL configuration. Update consent_forms through the bot config API.");
      }
      const hasHours = Object.prototype.hasOwnProperty.call(settings, "ttl_hours");
      if (hasHours && (!Number.isInteger(settings.ttl_hours) || settings.ttl_hours < 1 || settings.ttl_hours > 8760)) {
        throw new Error("Invalid consent TTL configuration. Update consent_forms through the bot config API.");
      }
      const value = Object.prototype.hasOwnProperty.call(settings, "ttl_minutes") ? settings.ttl_minutes
        : hasHours ? settings.ttl_hours * 60 : DEFAULT_TTL_MINUTES;
      if (!Number.isInteger(value) || value < 1 || value > 525600) {
        throw new Error("Invalid consent TTL configuration. Update consent_forms through the bot config API.");
      }
      if (settings.email_enabled !== undefined && typeof settings.email_enabled !== "boolean") {
        throw new Error("Invalid consent email configuration. Update consent_forms through the bot config API.");
      }
      if (settings.email_recipients !== undefined && typeof settings.email_recipients !== "string") {
        throw new Error("Invalid consent email configuration. Update consent_forms through the bot config API.");
      }
      const recipientValue = settings.email_recipients || "";
      const parsed = parseRecipients(recipientValue);
      if (parsed.error) {
        throw new Error("Invalid consent email configuration. Update consent_forms through the bot config API.");
      }
      setMinutes(String(value));
      setEmailEnabled(settings.email_enabled === true);
      setEmailRecipients(parsed.recipients.join(","));
      const template = settings.email_body_template === undefined ? DEFAULT_EMAIL_BODY : settings.email_body_template;
      if (bodyError(template)) throw new Error("Invalid consent email body template. Update consent_forms through the bot config API.");
      setEmailBody(template);
      if ((settings.otp_template_name !== undefined && (typeof settings.otp_template_name !== "string" || !/^[a-z0-9_]*$/.test(settings.otp_template_name)))
        || (settings.otp_template_language !== undefined && (typeof settings.otp_template_language !== "string" || !/^[a-z]{2,3}(?:_[A-Z]{2})?$/.test(settings.otp_template_language)))) {
        throw new Error("Invalid WhatsApp OTP template configuration.");
      }
      setOtpTemplateName(settings.otp_template_name || "");
      setOtpTemplateLanguage(settings.otp_template_language || "en_US");
    }).catch((err) => {
      if (active) {
        setLoadFailed(true);
        setError(err.message || "Unable to load consent settings.");
      }
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [botId, retry]);

  const ttl = Number(minutes);
  const invalid = !minutes.trim() || !Number.isInteger(ttl) || ttl < 1 || ttl > 525600;
  const parsedRecipients = parseRecipients(emailRecipients);
  const recipientsInvalid = Boolean(parsedRecipients.error) || (emailEnabled && parsedRecipients.recipients.length === 0);
  const templateError = bodyError(emailBody);
  const otpInvalid = !/^[a-z0-9_]*$/.test(otpTemplateName) || otpTemplateName.length > 512
    || !/^[a-z]{2,3}(?:_[A-Z]{2})?$/.test(otpTemplateLanguage);
  const locked = disabled || loading || saving || loadFailed;
  const save = async () => {
    if (locked || invalid || recipientsInvalid || templateError || otpInvalid) return;
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateTenantConfigs(botId, {
        config_key: "consent_forms",
        config_json: {
          ttl_minutes: ttl,
          email_enabled: emailEnabled,
          email_recipients: parsedRecipients.recipients.join(","),
          email_body_template: emailBody,
          otp_template_name: otpTemplateName,
          otp_template_language: otpTemplateLanguage,
        },
      });
      setSaved(true);
    } catch (err) {
      setError(typeof err.details?.detail === "string" ? err.details.detail : err.message || "Unable to save consent settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4" aria-label="Consent form access">
      <h3 className="text-sm font-semibold text-slate-900">Consent settings</h3>
      <p className="text-xs text-slate-500">
        Public consent links expire after this duration, measured from request creation, including time spent as a draft.
      </p>
      {loading ? <p role="status">Loading consent settings...</p> : (
        <fieldset disabled={locked} className="space-y-3">
          <section className="space-y-3 rounded-xl border border-slate-200 p-4" aria-label="WhatsApp OTP verification">
            <h4 className="text-sm font-semibold text-slate-900">WhatsApp OTP verification</h4>
            <p className="text-xs text-slate-500">
              Use an approved Authentication template with a Copy code button from this agent's WhatsApp Business Account.
              Leave the name blank to use the server's default template. The OTP secret stays configured on the server.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                OTP template name
                <input value={otpTemplateName} placeholder="consent_verification"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  onChange={(event) => { setOtpTemplateName(event.target.value); setSaved(false); }} />
              </label>
              <label className="block text-sm">
                OTP template language
                <input value={otpTemplateLanguage} placeholder="en_US"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  onChange={(event) => { setOtpTemplateLanguage(event.target.value); setSaved(false); }} />
              </label>
            </div>
            {otpInvalid && <p role="alert" className="text-sm text-rose-700">Use lowercase letters, numbers and underscores for the name, and a language code such as en or en_US.</p>}
          </section>
          <label className="block text-sm">
            Consent link TTL (minutes)
            <input type="number" min="1" max="525600" step="1" value={minutes}
              className="ml-3 rounded-lg border border-slate-200 px-3 py-2"
              onChange={(event) => { setMinutes(event.target.value); setSaved(false); }} />
          </label>
          {invalid && <p className="text-xs text-rose-700">Enter a whole number from 1 to 525600 minutes (one year).</p>}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={emailEnabled}
              onChange={(event) => { setEmailEnabled(event.target.checked); setSaved(false); }} />
            Email signed consent forms
          </label>
          <label className="block text-sm">
            Email recipients
            <textarea rows={2} value={emailRecipients} placeholder="records@example.com, clinic@example.com"
              className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2"
              onChange={(event) => { setEmailRecipients(event.target.value); setSaved(false); }} />
          </label>
          {parsedRecipients.error && <p className="text-xs text-rose-700">{parsedRecipients.error}</p>}
          {emailEnabled && !parsedRecipients.error && parsedRecipients.recipients.length === 0 && (
            <p className="text-xs text-rose-700">Add at least one email recipient when signed-consent email is enabled.</p>
          )}
          <p className="text-xs text-slate-500">Newly signed consent forms are emailed to these recipients. This does not send email for existing signed requests.</p>
          <div aria-label="Consent email editor and preview" className="grid gap-4 lg:grid-cols-2 lg:items-start">
            <div className="space-y-3">
              <label className="block text-sm">
                Consent email body
                <textarea ref={bodyInput} rows={12} value={emailBody} maxLength={10000}
                  className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2"
                  onChange={(event) => { setEmailBody(event.target.value); setSaved(false); }} />
              </label>
              <div className="flex flex-wrap gap-2" aria-label="Insert email placeholder">
                {Object.keys(SAMPLE_VALUES).map((key) => (
                  <button type="button" key={key} className="rounded border border-slate-200 px-2 py-1 text-xs"
                    onClick={() => {
                      const input = bodyInput.current;
                      const start = input?.selectionStart ?? emailBody.length;
                      const end = input?.selectionEnd ?? start;
                      const token = `{{ ${key} }}`;
                      setEmailBody(emailBody.slice(0, start) + token + emailBody.slice(end));
                      setSaved(false);
                      requestAnimationFrame(() => { input?.focus(); input?.setSelectionRange(start + token.length, start + token.length); });
                    }}>{`{{ ${key} }}`}</button>
                ))}
              </div>
              {templateError && <p role="alert" className="text-xs text-rose-700">{templateError}</p>}
              <p className="text-xs text-slate-500">
                Plain text only; line breaks are preserved in a professional HTML layout. The signed PDF is always attached.
                Patient details come from the stored consent snapshot. Templates are read when the emailer claims a delivery;
                changes can affect pending emails and retries, but not an email already in flight.
              </p>
              <button type="button" onClick={() => { setEmailBody(DEFAULT_EMAIL_BODY); setSaved(false); }}
                className="text-xs text-blue-700">Reset email body</button>
            </div>
            <div aria-label="Email body preview" className="min-h-72 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold text-slate-500">Preview with sample data</p>
              <p className="mt-2 whitespace-pre-wrap text-sm">
                {emailBody.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (token, key) => (
                  Object.prototype.hasOwnProperty.call(SAMPLE_VALUES, key) ? SAMPLE_VALUES[key] : token
                ))}
              </p>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Default: 2880 minutes. Applies only to new consent requests and replacements.
            Existing requests keep their expiry. Resending an expired, unsubmitted consent creates a new link with the current TTL starting at resend time.
            Staff cannot override individual consent expiry. Signed records and staff access are retained.
            Registration forms are unchanged.
          </p>
          <button type="button" onClick={save} disabled={locked || invalid || recipientsInvalid || Boolean(templateError) || otpInvalid}
            className="rounded-xl bg-[#096dea] px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
            {saving ? "Saving..." : "Save consent settings"}
          </button>
        </fieldset>
      )}
      {error && <div role="alert" className="text-sm text-rose-700">{error} <button type="button"
        disabled={saving} onClick={() => setRetry((value) => value + 1)}>Reload consent settings</button></div>}
      {saved && <p role="status" className="text-sm text-emerald-700">Consent settings saved.</p>}
    </section>
  );
}
